import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { abacGuard } from '@saraa/permissions/abacMiddleware.js';
import { Templates } from '@saraa/permissions/policies/index.js';
import { PAGE_CHECK } from '@saraa/permissions/policyHelper.js';
import { Template } from '../entities/Template.js';
import { loadTemplateTarget } from '../server/abacTargets.js';
import { resolveGetTemplateById, resolveSubscribeTemplates } from '../server/resolvers/resolveReadTemplates.js';
import {
  resolveCreateTemplate,
  resolveDeleteTemplate,
  resolveSetTemplateArchived,
  resolveUpdateTemplate,
} from '../server/resolvers/resolveWriteTemplate.js';

/**
 * Every way a template can be read or written. The generic entity mutations are
 * closed for this class, so these are it.
 *
 * Each endpoint states the permission it needs. For everything that addresses an
 * existing template, the guard loads that template and decides against *its*
 * organization; `create` is the one case where the organization comes from the
 * request, which is exactly the claim `isOrgAdminOf` is there to verify.
 */

const createTemplate = registerMutation({
  operationName: 'createTemplate',
  type: () => ID,
  params: [
    // Explicit rather than read off the entity: this is the value the guard
    // decides against, so it must be the same one the resolver writes.
    { name: 'organizationId', type: () => ID },
    { name: 'template', type: () => Template },
  ],
  middlewares: [
    abacGuard(Templates, 'create', (args: { organizationId: string }) => ({ organizationId: args.organizationId })),
  ],
  resolve: resolveCreateTemplate,
});

const updateTemplate = registerMutation({
  operationName: 'updateTemplate',
  type: () => Boolean,
  params: [{ name: 'template', type: () => Template }],
  middlewares: [
    abacGuard(Templates, 'modify', (args: { template: Template }) => loadTemplateTarget(args.template?._id)),
  ],
  resolve: resolveUpdateTemplate,
});

const deleteTemplate = registerMutation({
  operationName: 'deleteTemplate',
  type: () => Boolean,
  params: [{ name: 'templateId', type: () => ID }],
  middlewares: [abacGuard(Templates, 'delete', (args: { templateId: string }) => loadTemplateTarget(args.templateId))],
  resolve: resolveDeleteTemplate,
});

const setTemplateArchived = registerMutation({
  operationName: 'setTemplateArchived',
  type: () => Boolean,
  params: [
    { name: 'templateId', type: () => ID },
    { name: 'archived', type: () => Boolean },
  ],
  // Archiving takes a template out of use, so it answers to the same permission
  // as deleting it.
  middlewares: [abacGuard(Templates, 'delete', (args: { templateId: string }) => loadTemplateTarget(args.templateId))],
  resolve: resolveSetTemplateArchived,
});

const subscribeTemplates = registerSubscription({
  operationName: 'subscribeTemplates',
  type: () => [Template],
  // `(skip, limit, filter)` is the shape the data-view calls a list operation with.
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
  middlewares: [abacGuard(Templates, 'view', () => PAGE_CHECK)],
  resolve: resolveSubscribeTemplates,
});

const getTemplateById = registerQuery({
  operationName: 'getTemplateById',
  type: () => Template,
  params: [{ name: 'templateId', type: () => ID }],
  middlewares: [abacGuard(Templates, 'view', (args: { templateId: string }) => loadTemplateTarget(args.templateId))],
  resolve: resolveGetTemplateById,
});

export const TemplateAPI = {
  create: createTemplate,
  update: updateTemplate,
  delete: deleteTemplate,
  setArchived: setTemplateArchived,
  subscribe: subscribeTemplates,
  getById: getTemplateById,
};
